import { Delete, Upload } from '@mui/icons-material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Create from '@mui/icons-material/Create'
import {
	Box,
	Button,
	Fade,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Theme,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { percent } from 'csx'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import { AssignmentType } from '../../../../api/clients/workflowTasks/DTOs'
import { WorkflowDefinition } from '../../../../api/clients/workflows/DTOs'
import WorkflowsClient from '../../../../api/clients/workflows/WorkflowsClient'
import UserChip from '../../../../components/chips/UserChip'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import { FilterControl, SelectFilterOption } from '../../../../components/tables/filters/DTO'
import { usePrompts } from '../../../../services/notifications/PromptService'
import { toastService } from '../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { ConfirmDeleteWorkflowDialog } from './ConfirmDeleteWorkflowDialog'
import { CreateWorkflowDialog } from './CreateWorkflowDialog'
import { UploadWorkflowDialog } from './UploadWorkflowDialog'

type WorkflowsDashboardTableProps = {
	workflowDefinitions: WorkflowDefinition[]
	onViewWorkflowClicked: (workflowId: number) => void
	onDeleteWorkflow?: (workflowId: number) => void
}

export const WorkflowsDashboardPage = observer(() => {
	const client = new WorkflowsClient()

	const history = useHistory()
	const promptService = usePrompts()

	const localStore = useLocalObservable(() => ({
		createWorkflowOpen: false,
		uploadWorkflowOpen: false,
		publishedWorkflows: undefined as WorkflowDefinition[] | undefined
	}))

	const getPublishedWorkflows = async (): Promise<void> => {
		const response = await client.getAllWorkflows()
		let unnamedWorkflowCount = 1

		if (response.status === 200) {
			for (const workflowDefinition of response.data) {
				if (workflowDefinition.workflow.name === '') {
					workflowDefinition.workflow.name = `Unnamed Workflow ${unnamedWorkflowCount}`
					unnamedWorkflowCount += 1
				}
			}
			runInAction(() => localStore.publishedWorkflows = response.data)
		}
	}

	const handleUploadWorkflowClicked = action(() => {
		localStore.uploadWorkflowOpen = true
	})

	const handleUploadWorkflowClosed = action(() => {
		localStore.uploadWorkflowOpen = false
	})

	const handleUploadWorkflow = (wfData: WorkflowDefinition) => {
		const result = client.uploadWorkflowDefinition(wfData)

		result.then((v) => {
			history.push(`/_workflows/${v.data.workflowId}`)
		})

		runInAction(() => localStore.uploadWorkflowOpen = false)
	}

	const handleCreateWorkflowClicked = action(() => {
		localStore.createWorkflowOpen = true
	})

	const handleCreateWorkflowClosed = action(() => {
		localStore.createWorkflowOpen = false
	})

	const handleCreateWorkflow = (name: string) => {
		client
			.createWorkflow({
				workflowName: name,
				enabled: false,
			})
			.then((v) => v.data)
			.then((v) => {
				handleCreateWorkflowClosed()
				history.push(`/_workflows/${v.id}`)
			})
	}

	const handleViewWorkflowClicked = (workflowId: number) => {
		history.push(`/_administration/_workflows/${workflowId}`)
	}

	const handleDeleteWorkflow = async (workflowId: number) => {
		try {
			await client.deleteWorkflow(workflowId)

			const newPublishedWorkflows = (localStore.publishedWorkflows ?? []).filter(
				(v) => v.workflowId !== workflowId,
			)

			runInAction(() => localStore.publishedWorkflows = newPublishedWorkflows)

			toastService.displayToast({
				message: 'Workflow deleted',
				area: 'global',
				severity: 'success',
			})
		} catch (e) {
			console.log(e)
			toastService.displayToast({
				message: 'Error deleting workflow',
				area: 'global',
				severity: 'error',
			})
		}
	}

	useEffect(() => {
		getPublishedWorkflows()
	}, [])

	const getUserOptions = useMemo((): SelectFilterOption[] => {
		const userOptions: Record<string, unknown> = {}
		localStore.publishedWorkflows?.forEach((w) => userOptions[w.lastModifiedBy] = true)
		return Object.keys(userOptions).map((k) => ({
			label: <UserChip
				assignmentType={AssignmentType.User}
				id={Number(k)}
			/>,
			value: Number(k)
		}))
	}, [localStore.publishedWorkflows])

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'date range',
			name: 'lastModifiedDate',
			label: 'Last Edited',
			filterEntry: (w, filter) => {
				const lastModified = new Date((w as WorkflowDefinition).lastModifiedDate)
				return (w as WorkflowDefinition).version !== 0
					&& lastModified > ((filter as { start: Date, end: Date }).start)
					&& lastModified < ((filter as { start: Date, end: Date }).end)
			}
		},
		{
			type: 'multiselect',
			name: 'lastModifiedBy',
			label: "Edited By",
			options: getUserOptions
		},
		{
			type: 'search',
			name: 'search',
			filterEntry: (w, val) => (w as WorkflowDefinition).workflow.name.toUpperCase().includes((val as string).toUpperCase())
		}
	], [localStore.publishedWorkflows])

	const columns: TableColumn<WorkflowDefinition>[] = useMemo(() => [
		{
			name: "name",
			label: "Name",
			renderContent: (w) => w.workflow.name
		},
		{
			name: "version",
			label: "Version"
		},
		{
			name: "lastModifiedDate",
			label: "Last Edited",
			renderContent: (w) => w.version === 0 ? "-" :
				new Date(w.lastModifiedDate).toLocaleString()
		},
		{
			name: "lastModifiedBy",
			label: "Edited By",
			renderContent: (w) => w.version === 0 ? "-" :
				<UserChip
					assignmentType={AssignmentType.User}
					id={w.lastModifiedBy}
				/>
		},
		{
			renderHeading: <TableCell key={4} align="right">Actions</TableCell>,
			renderCell: (w) =>
				<TableCell key={4} align="right">
					{handleDeleteWorkflow !== undefined &&
						<IconButton
							onClick={() => {
								promptService
									.showDialog((props) => (
										<ConfirmDeleteWorkflowDialog
											name={w.workflow.name}
											onCancel={() => props.close('cancel')}
											onConfirm={() => props.close('okay')}
										/>
									))
									.then((v) => {
										if (v == 'okay')
											handleDeleteWorkflow!(
												w.workflowId,
											)
									})
							}}
						>
							<Delete fontSize="small" />
						</IconButton>
					}
					<IconButton
						onClick={() =>
							handleViewWorkflowClicked(
								w.workflowId,
							)
						}
					>
						<ArrowForwardIcon fontSize="small" />
					</IconButton>
				</TableCell>
		}
	], [])

	return (
		<AdministrationPageContainer
			title="Workflows"
			actions={
				<Box
					display="flex"
					justifyContent={'center'}
					alignItems={'center'}
					gap={2}
				>
					<Button
						variant="outlined"
						color="primary"
						onClick={handleUploadWorkflowClicked}
						startIcon={<Upload />}
					>
						Upload Workflow
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleCreateWorkflowClicked}
						startIcon={<Create />}
					>
						Create Workflow
					</Button>
				</Box>
			}
		>
			{localStore.publishedWorkflows === undefined ? (
				<FullscreenSpinner />
			) : (
				<FilteredTable
					allEntries={localStore.publishedWorkflows}
					columns={columns}
					filterControls={filterControls}
				/>
			)}

			<CreateWorkflowDialog
				open={localStore.createWorkflowOpen}
				onClose={handleCreateWorkflowClosed}
				existingWorkflowNames={
					localStore.publishedWorkflows?.map((v) => v.workflow.name) ?? []
				}
				onCreateWorkflow={handleCreateWorkflow}
			/>

			<UploadWorkflowDialog
				open={localStore.uploadWorkflowOpen}
				onClose={handleUploadWorkflowClosed}
				existingWorkflowNames={
					localStore.publishedWorkflows?.map((v) => v.workflow.name) ?? []
				}
				onCreateWorkflow={handleUploadWorkflow}
			></UploadWorkflowDialog>
		</AdministrationPageContainer>
	)
})

export const WorkflowsDashboardTable: React.FC<WorkflowsDashboardTableProps> =
	observer((props: WorkflowsDashboardTableProps) => {
		const styles = useStyles()
		const promptService = usePrompts()

		return (
			<Fade in>
				<TableContainer className={styles.tableRoot} component={Paper}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Version</TableCell>
								<TableCell>Last Edited</TableCell>
								<TableCell>Edited By</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{props.workflowDefinitions.map((workflowDefinition, i) => {
								return (
									<TableRow key={i}>
										<TableCell>{workflowDefinition.workflow.name}</TableCell>
										<TableCell>{workflowDefinition.version}</TableCell>
										{workflowDefinition.version === 0 ? (
											<>
												<TableCell>-</TableCell>
												<TableCell>-</TableCell>
											</>
										) : (
											<>
												<TableCell>
													{new Date(
														workflowDefinition.lastModifiedDate,
													).toLocaleString()}
												</TableCell>
												<TableCell>
													<UserChip
														id={workflowDefinition.lastModifiedBy}
														assignmentType={AssignmentType.User}
													/>
												</TableCell>
											</>
										)}
										<TableCell align="right">
											{props.onDeleteWorkflow !== undefined && (
												<IconButton
													onClick={() => {
														promptService
															.showDialog((props) => (
																<ConfirmDeleteWorkflowDialog
																	name={workflowDefinition.workflow.name}
																	onCancel={() => props.close('cancel')}
																	onConfirm={() => props.close('okay')}
																/>
															))
															.then((v) => {
																if (v == 'okay')
																	props.onDeleteWorkflow!(
																		workflowDefinition.workflowId,
																	)
															})
													}}
												>
													<Delete fontSize="small" />
												</IconButton>
											)}
											<IconButton
												onClick={() =>
													props.onViewWorkflowClicked(
														workflowDefinition.workflowId,
													)
												}
											>
												<ArrowForwardIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Fade>
		)
	})

const useStyles = makeStyles((theme: Theme) => ({
	tableRoot: {
		width: percent(100),
		marginBottom: theme.spacing(3),
		height: 'fit-content',
		overflow: 'auto',
	},
}))
